import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstanceWithIdentityProviderHeader } from "../../utilities/axios/axiosInstance";
import { environment_conf } from "../../utilities/environment";
import { CREDIT_EXCEED_ERROR_CODE } from "../../utilities/errors/ErrorStatusCodes";
import { RootState } from '../reducers';

export interface AuthState {
  isAuthenticated: boolean;
  initializationStatus: 'initialized' | 'initializing' | 'not-initialized';
  status: "idle" | "loading" | "failed";
  errorType: string | null;
}

export const initialState: AuthState = {
  isAuthenticated: false,
  initializationStatus: 'not-initialized',
  status: 'idle',
  errorType: null,
};

// Single async thunk for authentication that handles both login and auth check
export const authorizeUser = createAsyncThunk(
  'auth/authenticate',
  async (_, { rejectWithValue }) => {
    const url = `${environment_conf.base_app_domain}/authorization`;
    try {
      const response = await axiosInstanceWithIdentityProviderHeader.post(
        url
      );

      if (response.status === 200) {
        return { isAuthenticated: true };
      } else {
        return { isAuthenticated: false };
      }
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        const data = error.response?.data;
        if (data?.type) {
          return rejectWithValue({
            type: data?.type,
            message: data?.message || data?.detail || 'Credit limit exceeded'
          });
        }
        return { isAuthenticated: false };
      }
      return rejectWithValue(error.message || 'Authentication failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => {
      return { ...initialState };
    },
    clearError: (state) => {
      state.errorType = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.pending, (state) => {
        state.status = 'loading';
        state.initializationStatus = 'initializing';
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.initializationStatus = 'initialized';
        state.isAuthenticated = action.payload.isAuthenticated;
        state.errorType = null;
      })
      .addCase(authorizeUser.rejected, (state, action) => {
        state.status = 'failed';
        state.initializationStatus = 'initialized';
        state.isAuthenticated = false;

        // Handle credit exceed error specifically
        if (action.payload && typeof action.payload === 'object' && 'type' in action.payload) {
          console.log(action.payload);
          const errorPayload = action.payload as { type: string; message: string };
          state.errorType = errorPayload.type;
        } else {
          state.errorType = typeof action.payload === 'string' ? action.payload : 'Authentication failed';
        }
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.status === 'loading';
export const selectAuthInitializationStatus = (state: RootState) => state.auth.initializationStatus;
export const selectAuthStatus = (state: RootState) => state.auth.status;
export const selectAuthError = (state: RootState) => state.auth.errorType;
export const selectIsCreditExceededError = (state: RootState) => {
  const errorType = state.auth.errorType;
  return errorType === CREDIT_EXCEED_ERROR_CODE;
};